import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "buyer", organization: "" });
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const { t } = useLanguage();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register({ ...form, ...(ref ? { ref } : {}) });
      toast.success(`Account created — welcome ${u.name}`);
      nav(u.role === "seller" ? "/seller" : u.role === "buyer" ? "/buyer" : u.role === "service_provider" ? "/directory/me" : "/marketplace");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const on = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  return (
    <div className="max-w-md mx-auto px-6 py-12">
      <div className="surface-card p-8">
        <h1 className="heading text-2xl font-bold">{t("register.title")}</h1>
        <p className="text-sm text-stone-500 mt-1">{t("register.subtitle")}</p>
        {ref && <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2 mt-2" data-testid="register-ref-note">Invited by a GeneticsTrade member (code {ref}).</p>}
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label>{t("register.name")}</Label>
            <Input data-testid="register-name-input" required value={form.name} onChange={on("name")} />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="register-email-input" type="email" required value={form.email} onChange={on("email")} />
          </div>
          <div>
            <Label>Password</Label>
            <Input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={on("password")} />
          </div>
          <div>
            <Label>{t("register.role")}</Label>
            <Select value={form.role} onValueChange={on("role")}>
              <SelectTrigger data-testid="register-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="buyer">Buyer (breeder / farmer)</SelectItem>
                <SelectItem value="seller">Seller (stud / AI center)</SelectItem>
                <SelectItem value="service_provider">Service provider (transport / vet)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role !== "buyer" && (
            <div>
              <Label>Organization</Label>
              <Input data-testid="register-org-input" value={form.organization} onChange={on("organization")} />
            </div>
          )}
          <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="btn-earth w-full h-10">
            {loading ? "…" : t("register.submit")}
          </Button>
          <p className="text-xs text-stone-400 text-center">By creating an account you agree to the <Link to="/terms" className="underline">Terms &amp; Conditions</Link>, including the 6% sales fee.</p>
        </form>
        <p className="mt-6 text-sm text-stone-500 text-center">
          {t("register.have")} <Link to="/login" className="text-[color:var(--forest-deep)] font-semibold underline">{t("login.title")}</Link>
        </p>
      </div>
    </div>
  );
}
